import { CurrencyPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CommissionReport } from '@models/reports/commission/commission-report';
import { IReportComponent } from '@models/reports/ireport-component';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { DataSourceRequestState, groupBy } from '@progress/kendo-data-query';
import { ReportsService } from '@services/reports.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

class User {
  id: string;
  name: string;
  constructor(comm: CommissionReport) {
    this.id = comm.userId;
    this.name = comm.fullName;
  }
}

class Row {
  name: string;
  serviceId: number;
  clinicSupplyId?: number;
  clinicSupplyName?: string;
  clinicSupplyTypeId?: number;
  clinicSupplyTypeName?: string;

  constructor(comm: CommissionReport) {
    this.serviceId = comm.serviceTemplateId;
    this.name = comm.serviceAltName;
    this.clinicSupplyId = comm.observationListId;
    this.clinicSupplyName = comm.observationListName;
    this.clinicSupplyTypeId = comm.observationListTypeId;
    this.clinicSupplyTypeName = comm.observationListTypeName;
  }

  get id() {
    return this.serviceId.toString() + (this.clinicSupplyId ?? 0).toString();
  }
}

class Cell {
  isFlatRate: boolean;
  serviceCount: number;
  sales: number;
  rate: number;

  constructor(comm: CommissionReport) {
    this.isFlatRate = comm.isFlatRate;
    this.serviceCount = comm.serviceCount;
    this.sales = comm.totalSum;
    this.rate = comm.commission;
  }

  get due() {
    return this.isFlatRate ? this.serviceCount * this.rate : (this.sales * this.rate) / 100;
  }
}

type NestedMap<T> = Map<string, Map<string, T>>;

@Component({
  selector: 'app-commissions-reports',
  templateUrl: './commissions-reports.component.html',
  styleUrls: ['./commissions-reports.component.less'],
  encapsulation: ViewEncapsulation.None,
})
export class CommissionsReportsComponent implements OnInit, OnDestroy, IReportComponent {
  gridView: GridDataResult;
  gridState: DataSourceRequestState;
  loading: boolean = false;
  unsub: Subject<void> = new Subject<void>();
  distinctUsers: User[];

  private commCells: NestedMap<Cell>;
  private subTotals: NestedMap<number>;
  private totals: Map<string, number>;
  private categoryOrder = ['Toxins', 'Fillers', 'CoolSculpting Applicators', 'Needles', 'Deoxycholate', null];

  constructor(private reportsService: ReportsService, private currencyPipe: CurrencyPipe) {}

  ngOnInit(): void {
    this.initDefaultValue();
    this.getReportData();
  }

  initDefaultValue(): void {
    this.gridState = {
      skip: 0,
      take: 100,
      sort: [],
      group: [],
    };
  }

  getReportData(): void {
    this.loading = true;
    this.reportsService
      .getCommissionsReportEntities(this.gridState)
      .pipe(takeUntil(this.unsub))
      .subscribe((commissionsReports: GridDataResult) => {
        this.gridView = this.processResults(commissionsReports);
        this.loading = false;
      });
  }

  getSales(dataItem: Row, user: User): string {
    let cell = this.commCells.get(dataItem.id).get(user.id);
    if (!cell) return 'None';
    return cell.isFlatRate ? cell.serviceCount.toString() : this.currencyPipe.transform(cell.sales);
  }

  getCommission(service: Row, user: User): string {
    const cell = this.commCells.get(service.id).get(user.id);
    if (!cell) return '';
    return cell.isFlatRate
      ? cell.rate.toLocaleString(undefined, {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 2,
        })
      : (cell.rate / 100).toLocaleString(undefined, {
          style: 'percent',
          maximumFractionDigits: 1,
        });
  }

  getDue(service: Row, user: User): string {
    const cell = this.commCells.get(service.id).get(user.id);
    if (!cell) return '$0.00';
    return this.currencyPipe.transform(cell.due);
  }

  getSubTotal(group, user: User): number {
    return this.subTotals.get(group.value).get(user.id) ?? 0;
  }

  getTotal(user: User): number {
    return this.totals.get(user.id) ?? 0;
  }

  private processResults(result: GridDataResult): GridDataResult {
    const commissions: CommissionReport[] = result.data;
    const distinctRows = this.getDistinct(commissions);

    const grouped = groupBy(distinctRows, [
      {
        field: 'clinicSupplyTypeName',
        dir: 'desc',
      },
    ]);

    const groupedSorted = grouped.sort((a, b) => {
      let rankA = this.categoryOrder.indexOf(a.value);
      let rankB = this.categoryOrder.indexOf(b.value);
      return rankA - rankB;
    });

    return { data: groupedSorted, total: grouped.length };
  }

  private getDistinct(commissions: CommissionReport[]): Row[] {
    const distinctUsers: User[] = [];
    const distinctRows: Row[] = [];
    const cells: NestedMap<Cell> = new Map();
    const subTotals: NestedMap<number> = new Map();
    const totals: Map<string, number> = new Map();

    commissions.forEach((comm) => {
      const commRow = new Row(comm);
      const commCell = new Cell(comm);

      const rowDistinct = !distinctRows.some((dr) => dr.id == commRow.id);
      if (rowDistinct) distinctRows.push(commRow);

      const userDistinct = !distinctUsers.some((du) => du.id == comm.userId);
      if (userDistinct) distinctUsers.push(new User(comm));

      if (!cells.has(commRow.id)) cells.set(commRow.id, new Map());
      cells.get(commRow.id).set(comm.userId, commCell);

      if (!subTotals.has(commRow.clinicSupplyTypeName)) subTotals.set(commRow.clinicSupplyTypeName, new Map());
      if (!subTotals.get(commRow.clinicSupplyTypeName).has(comm.userId))
        subTotals.get(commRow.clinicSupplyTypeName).set(comm.userId, 0);
      const currentSubTotal = subTotals.get(commRow.clinicSupplyTypeName).get(comm.userId);
      subTotals.get(commRow.clinicSupplyTypeName).set(comm.userId, currentSubTotal + commCell.due);

      if (!totals.has(comm.userId)) totals.set(comm.userId, 0);
      const currentTotal = totals.get(comm.userId);
      totals.set(comm.userId, currentTotal + commCell.due);
    });

    this.distinctUsers = distinctUsers;
    this.commCells = cells;
    this.subTotals = subTotals;
    this.totals = totals;
    return distinctRows;
  }

  exportToExcel(): void {
    this.exportToCSV();
  }

  private exportToCSV(): void {
    const gridState: DataSourceRequestState = {};
    this.reportsService.getCommissionsReportEntities(gridState).subscribe((commissionsReports: GridDataResult) => {
      const commissions: CommissionReport[] = commissionsReports.data;
      const csvData = this.generateCSVData(commissions);
      this.downloadCSV(csvData, 'commissions_report.csv');
    });
  }

  private generateCSVData(commissions: CommissionReport[]): string {
    const distinctUsers: User[] = [];
    const distinctRows: Row[] = [];
    const cells: NestedMap<Cell> = new Map();
    const totals: Map<string, number> = new Map();

    // Process data
    commissions.forEach((comm) => {
      const commRow = new Row(comm);
      const commCell = new Cell(comm);

      const rowDistinct = !distinctRows.some((dr) => dr.id == commRow.id);
      if (rowDistinct) distinctRows.push(commRow);

      const userDistinct = !distinctUsers.some((du) => du.id == comm.userId);
      if (userDistinct) distinctUsers.push(new User(comm));

      if (!cells.has(commRow.id)) cells.set(commRow.id, new Map());
      cells.get(commRow.id).set(comm.userId, commCell);

      if (!totals.has(comm.userId)) totals.set(comm.userId, 0);
      const currentTotal = totals.get(comm.userId);
      totals.set(comm.userId, currentTotal + commCell.due);
    });

    // Generate CSV headers
    const headers = ['Service', 'Clinic Supply', 'Category'];
    distinctUsers.forEach((user) => {
      headers.push(`${user.name} - Sales`);
      headers.push(`${user.name} - Rate`);
      headers.push(`${user.name} - Due`);
    });

    // Generate CSV rows
    const rows: string[] = [];
    
    // Add header row
    rows.push(this.escapeCSV(headers.join(',')));

    // Add data rows
    distinctRows.forEach((row) => {
      const rowData: string[] = [
        row.name,
        row.clinicSupplyName || '',
        row.clinicSupplyTypeName || 'No Clinic Supply'
      ];

      distinctUsers.forEach((user) => {
        const cell = cells.get(row.id)?.get(user.id);
        if (cell) {
          const sales = cell.isFlatRate
            ? cell.serviceCount.toString()
            : this.currencyPipe.transform(cell.sales)?.replace('$', '').replace(',', '') || '0';
          const rate = cell.isFlatRate
            ? this.currencyPipe.transform(cell.rate)?.replace('$', '').replace(',', '') || '0'
            : (cell.rate / 100).toFixed(1) + '%';
          const due = this.currencyPipe.transform(cell.due)?.replace('$', '').replace(',', '') || '0';
          rowData.push(sales, rate, due);
        } else {
          rowData.push('None', '', '0');
        }
      });

      rows.push(this.escapeCSV(rowData.join(',')));
    });

    // Add totals row
    const totalRow: string[] = ['TOTAL', '', ''];
    distinctUsers.forEach((user) => {
      const total = totals.get(user.id) ?? 0;
      const totalFormatted = this.currencyPipe.transform(total)?.replace('$', '').replace(',', '') || '0';
      totalRow.push('', '', totalFormatted);
    });
    rows.push(this.escapeCSV(totalRow.join(',')));

    return rows.join('\n');
  }

  private escapeCSV(csvRow: string): string {
    // Escape CSV values that contain commas, quotes, or newlines
    return csvRow.split(',').map(field => {
      if (field.includes(',') || field.includes('"') || field.includes('\n')) {
        return `"${field.replace(/"/g, '""')}"`;
      }
      return field;
    }).join(',');
  }

  private downloadCSV(csvContent: string, filename: string): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }


  ngOnDestroy(): void {
    this.unsub.next();
    this.unsub.complete();
  }
}
